// ============================================================
// OliviumErrorBoundary
//
// Class component because React error boundaries cannot be
// implemented as hooks. Catches render errors below it and
// renders a calm fallback that lets the operator retry without
// losing the surrounding shell.
//
// We deliberately do NOT swallow errors. componentDidCatch logs
// to console so Sentry's global handler still picks them up,
// and so live deploys still surface them in the operator's
// browser devtools.
// ============================================================

import { Component } from 'react'

export default class OliviumErrorBoundary extends Component {
  constructor(props) {
    super(props)
    this.state = { error: null }
  }

  static getDerivedStateFromError(error) {
    return { error }
  }

  componentDidCatch(error, info) {
    // Don't swallow — Sentry's global handler is still listening.
    // eslint-disable-next-line no-console
    console.error('[OLIVIUM:boundary]', error, info?.componentStack)
  }

  reset = () => this.setState({ error: null })

  render() {
    const { error } = this.state
    const { fallback, scope = 'console', children } = this.props
    if (!error) return children

    if (typeof fallback === 'function') return fallback({ error, reset: this.reset })

    return (
      <div style={{
        margin: 24,
        padding: 20,
        borderRadius: 'var(--olv-radius-panel)',
        background: 'rgba(255,59,92,0.05)',
        border: '1px solid rgba(255,59,92,0.20)',
        backdropFilter: 'var(--olv-panel-blur)',
        fontFamily: 'var(--font-body)',
        color: 'var(--olv-text-primary)',
        maxWidth: 560,
      }}>
        <div style={{
          fontSize: 9,
          letterSpacing: '0.12em',
          color: 'var(--olv-red)',
          fontFamily: 'var(--font-mono)',
          marginBottom: 8,
        }}>
          OLIVIUM · {scope.toUpperCase()} · RENDER FAULT
        </div>
        <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 6 }}>
          The {scope} surface stopped rendering.
        </div>
        <div style={{
          fontSize: 11,
          color: 'var(--olv-text-secondary)',
          fontFamily: 'var(--font-mono)',
          marginBottom: 14,
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
        }}>
          {String(error?.message || error)}
        </div>
        <button
          onClick={this.reset}
          style={{
            padding: '6px 14px',
            borderRadius: 6,
            border: '1px solid rgba(124,92,252,0.30)',
            background: 'rgba(124,92,252,0.08)',
            color: 'var(--olv-purple)',
            fontFamily: 'var(--font-mono)',
            fontSize: 11,
            cursor: 'pointer',
          }}
        >
          Retry
        </button>
      </div>
    )
  }
}
